// Library Methods
import {
  createContext,
  useCallback,
  useState,
  useMemo,
  useContext,
  useEffect,
  useRef,
} from "react";
import { HubConnection } from "@microsoft/signalr";
import { useNavigate } from "react-router-dom";

// Assets
import blackoutImage from "src/assets/images/live-update/blackLive.jpeg";
import notificationSound from "src/assets/sounds/notification.wav";

// Constants
import {
  algorithmTypes,
  DeviceStagesTimeline,
  examCardEnums,
} from "src/constants/enums";

// Hooks
import { useExamStatus } from "src/hooks/exams/useExamStatus";
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import useLiveDataStream from "src/hooks/connection/useLiveDataStream";

// Utils
import { getActiveStep } from "src/utils/signalR/signalRHelper";

// Types
import {
  ExamSectionReportType,
  ExamSectionType,
  ExamStatusType,
  ExamType,
} from "src/services/exams/types";
import { DeviceType } from "src/services/devices/types";

// Contexts
import { useDevicesContext } from "../devices/DevicesContext";
import { useClinicContext } from "../clinic/ClinicProvider";

// Services
import {
  subscribeToDevice,
  unsubscribeFromDevice,
} from "src/services/signalR/api";

const LiveUpdateContext = createContext({
  exam: {} as ExamType | undefined,
  currentExamSection: {} as ExamSectionType | undefined,
  open: false,
  deviceId: "" as string | null,
  device: {} as DeviceType | undefined,
  examStatus: { status: undefined, timelineStage: undefined } as ExamStatusType,
  isVrMode: false,
  isPaused: false,
  isConfirmation: false,
  isNotStarted: false,
  isTerminated: false,
  isRightCompleted: false,
  inTutorial: false,
  inCalibration: false,
  isOnline: false,
  bothControllersDisconnected: false,
  hmdInProximity: false,
  activeStep: { index: 0, value: 0 },
  possibleSteps: [0],
  examReportLive: {} as ExamSectionReportType | undefined,
  percentage: 0,
  shouldDisplayNavigation: false,
  fullScreenModal: false,
  peerConnection: null as RTCPeerConnection | null,
  dataChannel: null as RTCDataChannel | null,
  imageSrc: "",
  isVideo: false,
  isIOLSimulation: false,
  isProgressiveSimulation: false,
  isDryEyeSimulation: false,
  isVisualField: false,
  isEsterman: false,
  isVisualAcuity: false,
  isColorVisionD15: false,
  isColorVisionIshihara: false,
  isVfScreening: false,
  isPelliRobson: false,
  isTumbling: false,
  isLandoltC: false,
  isBaileyLovie: false,
  isETDRS: false,
  isRAPD: false,
  isOcularMotility: false,
  isSnellen: false,
  isFull120: false,
  isSuperior36: false,
  isSynoptophore: false,
  isAniseikonia: false,
  canDisplayEyeStream: false,
  actions: {
    stopWebRTC: () => {},
    setImageSrc: (_: string) => {},
    setDataChannel: (_: RTCDataChannel | null) => {},
    setPeerConnection: (_: RTCPeerConnection | null) => {},
    setFullScreenModal: (_: any) => {},
    setExamReportLive: (_: undefined) => {},
    setIsVrMode: (_: boolean) => {},
    setIsPaused: (_: boolean) => {},
    setPercentage: (_: number) => {},
    setIsConfirmation: (_: boolean) => {},
    setIsNotStarted: (_: boolean) => {},
    setIsTerminated: (_: boolean) => {},
    setIsRightCompleted: (_: boolean) => {},
    setShouldDisplayNavigation: (_: boolean) => {},
    handleViewLiveUpdateClick: (_: string) => {},
    handleNavigate: () => {},
    handleCloseModal: () => {},
    handleSubscribeToDevice: (
      _: string,
      __: HubConnection,
      ___: () => Promise<void>
    ) => {},
    handleUnsubscribeFromDevice: (_: string, __: HubConnection) => {},
  },
});

export const useLiveUpdateContext = () => {
  const context = useContext(LiveUpdateContext);
  if (!context) {
    throw new Error(
      "useLiveUpdateContext must be used within LiveUpdateContextProvider"
    );
  }

  return context;
};

type LiveUpdateContextProviderType = {
  defaultProps?: any;
  children: React.ReactNode;
};

const LiveUpdateContextProvider = ({
  defaultProps, // used mainly for testing
  children,
}: LiveUpdateContextProviderType) => {
  const navigate = useNavigate();
  const { getToken } = useTokenAccess();

  const { clinicInfo } = useClinicContext();
  const { devices } = useDevicesContext();

  // Modal values
  const [open, setOpen] = useState(defaultProps?.open ?? false);
  const [fullScreenModal, setFullScreenModal] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const [shouldDisplayNavigation, setShouldDisplayNavigation] = useState(false);
  const [examReportLive, setExamReportLive] = useState<
    ExamSectionReportType | undefined
  >(undefined);
  const [isVrMode, setIsVrMode] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [isNotStarted, setIsNotStarted] = useState(false);
  const [isRightCompleted, setIsRightCompleted] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const notificationBufferRef = useRef<AudioBuffer | null>(null);

  useEffect(() => {
    const unlock = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") {
        ctx.resume();
      }
      if (!notificationBufferRef.current) {
        fetch(notificationSound)
          ?.then((r) => r.arrayBuffer())
          ?.then((buf) => ctx.decodeAudioData(buf))
          ?.then((decoded) => {
            notificationBufferRef.current = decoded;
          })
          ?.catch(console.error);
      }
      document.removeEventListener("click", unlock);
      document.removeEventListener("keydown", unlock);
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("touchstart", unlock);
    };

    document.addEventListener("click", unlock);
    document.addEventListener("keydown", unlock);
    document.addEventListener("pointerdown", unlock);
    document.addEventListener("touchstart", unlock);

    return () => {
      document.removeEventListener("click", unlock);
      document.removeEventListener("keydown", unlock);
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("touchstart", unlock);
    };
  }, []);

  // WebRtc
  const [peerConnection, setPeerConnection] =
    useState<RTCPeerConnection | null>(null);
  const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
  const [imageSrc, setImageSrc] = useState(blackoutImage);

  // values that persist after the exam is complete
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [reportUrl, setReportUrl] = useState<string | null>(null);

  // current device data
  const device: DeviceType | undefined = useMemo(() => {
    const deviceWithExam = devices?.find(
      (examDevice) => examDevice?.id === deviceId
    );
    return defaultProps?.device ?? deviceWithExam ?? undefined;
  }, [defaultProps, devices, deviceId]);

  // current exam data
  const exam: ExamType | undefined = useMemo(() => {
    const deviceWithExam = devices?.find(
      (examDevice) => examDevice?.id === deviceId
    );
    const examToUse =
      defaultProps?.exam ?? deviceWithExam?.associatedExam?.exam ?? undefined;

    // update states that will persist after the exam is complete
    if (examToUse && open) {
      const currentUrl = `/patients/${examToUse?.patient?.id}/exam/${examToUse?.id}`;
      if (currentUrl !== reportUrl) {
        setReportUrl(currentUrl);
      }
    }

    return examToUse;
  }, [defaultProps, devices, deviceId, reportUrl, open]);

  const currentExamSection = useMemo(() => exam?.nextSection, [exam]);

  const examType = useMemo(
    () => currentExamSection?.type,
    [currentExamSection]
  );
  const examAlgorithm = useMemo(
    () => currentExamSection?.algorithm,
    [currentExamSection]
  );

  // the possible steps
  // remove the calibration stage option, when the exam has "calibration" as false
  // remove the tutorial stage option, when the exam has "requiresTutorial" as false
  const possibleSteps: Array<number> = useMemo(
    () =>
      Object.values(DeviceStagesTimeline)
        .filter(
          (stage) =>
            stage !== DeviceStagesTimeline.Calibration ||
            (stage === DeviceStagesTimeline.Calibration &&
              currentExamSection?.calibration)
        )
        .filter(
          (stage) =>
            stage !== DeviceStagesTimeline.Tutorial ||
            (stage === DeviceStagesTimeline.Tutorial &&
              currentExamSection?.requiresTutorial)
        )
        .filter((stage) => stage !== DeviceStagesTimeline.Terminated)
        .flatMap((value) => (typeof value === "number" ? [value] : [])), // remove undefined values
    [currentExamSection]
  );

  const { getExamStatus } = useExamStatus();
  const examStatus: ExamStatusType = useMemo(
    () => getExamStatus({ exam, device }),
    [device, exam, getExamStatus]
  );

  const activeStep: {
    index: number;
    value: number;
  } = useMemo(
    () =>
      defaultProps?.activeStep ??
      getActiveStep({ exam, possibleSteps, examStatus }),
    [exam, possibleSteps, examStatus, defaultProps?.activeStep]
  );

  const isOnline = useMemo(
    () => Boolean(device?.deviceStatus?.stage?.stageType),
    [device?.deviceStatus?.stage?.stageType]
  );

  const inTutorial = useMemo(
    () => activeStep.value === DeviceStagesTimeline.Tutorial,
    [activeStep.value]
  );

  const inCalibration = useMemo(
    () => activeStep.value === DeviceStagesTimeline.Calibration,
    [activeStep.value]
  );

  const bothControllersDisconnected = useMemo(
    () =>
      device?.deviceStatus?.controllerBatteryL === -1 &&
      device?.deviceStatus?.controllerBatteryR === -1,
    [
      device?.deviceStatus?.controllerBatteryL,
      device?.deviceStatus?.controllerBatteryR,
    ]
  );

  const hmdInProximity = useMemo(
    () => Boolean(device?.deviceStatus?.hmdInProximity),
    [device?.deviceStatus?.hmdInProximity]
  );

  const isVideo = useMemo(() => {
    return examType === examCardEnums.VIDEO.type;
  }, [examType]);

  const isIOLSimulation = useMemo(() => {
    return examType === examCardEnums.IOL_SIMULATION.type;
  }, [examType]);

  const isProgressiveSimulation = useMemo(() => {
    return examType === examCardEnums.PROGRESSIVE_SIMULATION.type;
  }, [examType]);

  const isDryEyeSimulation = useMemo(() => {
    return examType === examCardEnums.DRY_EYE_SIMULATION.type;
  }, [examType]);

  const isAnyVisualField = useMemo(() => {
    return Boolean(examType?.startsWith("VisualField"));
  }, [examType]);

  const isVisualField = useMemo(() => {
    return Boolean(
      examType?.startsWith("VisualField") &&
        examType !== examCardEnums.VISUAL_FIELD_ESTERMAN.type
    );
  }, [examType]);

  const isEsterman = useMemo(() => {
    return examType === examCardEnums.VISUAL_FIELD_ESTERMAN.type;
  }, [examType]);

  const isVisualAcuity = useMemo(() => {
    return examType === examCardEnums.VISUAL_ACUITY.type;
  }, [examType]);

  const isColorVisionD15 = useMemo(() => {
    return Boolean(
      examType === examCardEnums.COLOR_VISION.type &&
        examAlgorithm?.includes("D_15")
    );
  }, [examType, examAlgorithm]);

  const isColorVisionIshihara = useMemo(() => {
    return Boolean(
      examType === examCardEnums.COLOR_VISION.type &&
        examAlgorithm?.includes("ISHIHARA")
    );
  }, [examType, examAlgorithm]);

  const isVfScreening = useMemo(() => {
    return examAlgorithm === algorithmTypes.SCREEN_FAST;
  }, [examAlgorithm]);

  const isPelliRobson = useMemo(() => {
    return examAlgorithm === algorithmTypes.PELLI_ROBSON;
  }, [examAlgorithm]);

  const isTumbling = useMemo(() => {
    return examAlgorithm === algorithmTypes.TUMBLING_E;
  }, [examAlgorithm]);

  const isLandoltC = useMemo(() => {
    return examAlgorithm === algorithmTypes.LANDOLT_C;
  }, [examAlgorithm]);

  const isBaileyLovie = useMemo(() => {
    return examAlgorithm === algorithmTypes.BAILEY_LOVIE;
  }, [examAlgorithm]);

  const isETDRS = useMemo(() => {
    return examAlgorithm === algorithmTypes.ETDRS;
  }, [examAlgorithm]);

  const isSnellen = useMemo(() => {
    return examAlgorithm === algorithmTypes.SNELLEN;
  }, [examAlgorithm]);

  const isFull120 = useMemo(() => {
    return examAlgorithm === algorithmTypes.FULL_120;
  }, [examAlgorithm]);

  const isSuperior36 = useMemo(() => {
    return examAlgorithm === algorithmTypes.SUPERIOR_36;
  }, [examAlgorithm]);

  const isRAPD = useMemo(() => {
    return examType === examCardEnums.RAPD.type;
  }, [examType]);

  const isOcularMotility = useMemo(() => {
    return examType === examCardEnums.OCULAR_MOTILITY.type;
  }, [examType]);

  const isSynoptophore = useMemo(() => {
    return (
      examType === examCardEnums.DEVIATION_AND_CORRESPONDENCE.type ||
      examType === examCardEnums.FUSION_AND_VERGENCE.type ||
      examType === examCardEnums.STEREOPSIS.type
    );
  }, [examType]);

  const isAniseikonia = useMemo(() => {
    return examType === examCardEnums.ANISEIKONIA.type;
  }, [examType]);

  const canDisplayEyeStream = useMemo(() => {
    return (
      Boolean(clinicInfo?.licensing?.canUseAdvancedEyeTracking) &&
      (isAnyVisualField || isRAPD || isOcularMotility)
    );
  }, [
    clinicInfo?.licensing?.canUseAdvancedEyeTracking,
    isAnyVisualField,
    isRAPD,
    isOcularMotility,
  ]);

  // values to reset when closing the modal
  const resetTasks = useCallback(() => {
    setDeviceId(null);
    setIsVrMode(false);
    setExamReportLive(undefined);
    setPercentage(0);
    setIsPaused(false);
    setIsNotStarted(false);
    setIsConfirmation(false);
    setIsRightCompleted(false);
    setIsTerminated(false);
    setShouldDisplayNavigation(false);
  }, []);

  const handleViewLiveUpdateClick = useCallback(
    (deviceId: string) => {
      const examNotStarted = activeStep.index === -1;

      setDeviceId(deviceId);
      setIsVrMode(examNotStarted);
      setOpen(true);
    },
    [setOpen, setDeviceId, setIsVrMode, activeStep.index]
  );

  // Stop WebRTC connection
  const stopWebRTC = useCallback(() => {
    // Close the data channel
    if (dataChannel) {
      dataChannel.close();
      setDataChannel(null);
    }

    // Close the peer connection
    if (peerConnection) {
      peerConnection.close();
      setPeerConnection(null);
    }

    // Clear display
    setImageSrc(blackoutImage);
  }, [dataChannel, peerConnection, setDataChannel, setPeerConnection]);

  useLiveDataStream({
    clinicId: clinicInfo?.id,
    deviceId,
    open,
    onSnapshot: setExamReportLive,
  });

  const handleSubscribeToDevice = useCallback(
    async (
      deviceId: string,
      connection: HubConnection,
      refetchExams: () => Promise<void>
    ) => {
      try {
        const token = await getToken();
        const response = await subscribeToDevice(token, deviceId, connection);

        const data = JSON.parse(response);
        if (data?.state === true) {
          // exam completion listeners
          connection?.off("DeviceExamSubmitted");
          connection?.on("DeviceExamSubmitted", function (data) {
            refetchExams();

            const parseableString = data
              .replace(/\\/g, "")
              .replace(/"{/g, "{")
              .replace(/}"/g, "}");

            const deviceData = JSON.parse(parseableString);

            // play ding sound every time an exam is completed
            if (clinicInfo?.settings?.enablePortalSounds) {
              const ctx = audioCtxRef.current;
              const buffer = notificationBufferRef.current;
              if (ctx && buffer) {
                const source = ctx.createBufferSource();
                source.buffer = buffer;
                source.connect(ctx.destination);
                source.start();
              } else {
                new Audio(notificationSound).play().catch(console.error);
              }
            }

            if (open && deviceData?.data === deviceId) {
              setShouldDisplayNavigation(true);
              setOpen(false);
            }
          });
        }
      } catch (error) {
        console.error("Error while subscribing", error);
      }
    },
    [
      open,
      clinicInfo?.settings?.enablePortalSounds,
      getToken,
      setShouldDisplayNavigation,
      setOpen,
    ]
  );

  const handleUnsubscribeFromDevice = useCallback(
    async (deviceId: string, connection: HubConnection) => {
      try {
        const response = await unsubscribeFromDevice(deviceId, connection);
        const data = JSON.parse(response);
        if (data?.state === true) {
          setExamReportLive(undefined);

          // NOTE: do not remove connection listeners here, this will interfere with
          // the listener logic that runs when the live update modal is closed
        }
      } catch (error) {
        console.error("Could not unsubscribe from device", error);
      }
    },
    [setExamReportLive]
  );

  const handleCloseModal = useCallback(() => {
    if (peerConnection) stopWebRTC();

    resetTasks();
    setOpen(false);
  }, [resetTasks, setOpen, stopWebRTC, peerConnection]);

  const handleNavigate = useCallback(() => {
    handleCloseModal();

    if (reportUrl) navigate(reportUrl);
  }, [handleCloseModal, navigate, reportUrl]);

  return (
    <LiveUpdateContext.Provider
      value={{
        exam,
        currentExamSection,
        open,
        deviceId,
        device,
        examStatus,
        isVrMode,
        isPaused,
        isConfirmation,
        isNotStarted,
        isTerminated,
        isRightCompleted,
        inTutorial,
        inCalibration,
        isOnline,
        bothControllersDisconnected,
        hmdInProximity,
        activeStep,
        possibleSteps,
        examReportLive,
        percentage,
        shouldDisplayNavigation,
        fullScreenModal,
        peerConnection,
        dataChannel,
        imageSrc,
        isIOLSimulation,
        isProgressiveSimulation,
        isDryEyeSimulation,
        isVisualField,
        isEsterman,
        isVisualAcuity,
        isColorVisionD15,
        isColorVisionIshihara,
        isVfScreening,
        isPelliRobson,
        isTumbling,
        isLandoltC,
        isBaileyLovie,
        isETDRS,
        isRAPD,
        isOcularMotility,
        isSnellen,
        isFull120,
        isSuperior36,
        isSynoptophore,
        isAniseikonia,
        isVideo,
        canDisplayEyeStream,
        actions: {
          stopWebRTC,
          setImageSrc,
          setDataChannel,
          setPeerConnection,
          setFullScreenModal,
          setExamReportLive,
          setIsVrMode,
          setIsPaused,
          setPercentage,
          setIsConfirmation,
          setIsNotStarted,
          setIsTerminated,
          setIsRightCompleted,
          setShouldDisplayNavigation,
          handleViewLiveUpdateClick,
          handleCloseModal,
          handleNavigate,
          handleSubscribeToDevice,
          handleUnsubscribeFromDevice,
        },
      }}
    >
      {children}
    </LiveUpdateContext.Provider>
  );
};

export default LiveUpdateContextProvider;
